import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { supabase } from '../../lib/supabase';

const AdminHeader = () => {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user?.email) {
        setUserEmail(user?.email);
      }
    };
    
    getUser();
  }, []);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    
    setIsLoggingOut(true);
    
    try {
      const { error } = await supabase?.auth?.signOut();
      
      if (error) {
        console.error('Logout error:', error);
        alert(`Logout failed: ${error?.message}`);
        setIsLoggingOut(false);
        return;
      }

      // Clear any local state
      setUserEmail('');
      setShowUserMenu(false);
      
      // Navigate to login page
      navigate('/admin-authentication-portal', { replace: true });
    } catch (err) {
      console.error('Unexpected logout error:', err);
      alert('An unexpected error occurred during logout');
      setIsLoggingOut(false);
    }
  };

  const handleNavigate = (path) => {
    setShowUserMenu(false);
    navigate(path);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-card border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg transition-smooth hover:bg-primary/20">
              <Icon name="Truck" size={24} color="var(--color-primary)" strokeWidth={2} />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-semibold text-foreground">RPM Admin Dashboard</span>
              <span className="text-xs text-muted-foreground hidden sm:block">Fleet Manager Admin</span>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="hidden md:flex items-center space-x-2 px-3 py-1.5 bg-success/10 text-success text-xs font-medium rounded-md">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
              <span>System Online</span>
            </div>

            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 px-3 py-2 bg-accent/50 hover:bg-accent rounded-lg transition-colors"
              >
                <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-full">
                  <Icon name="User" size={16} color="var(--color-primary)" strokeWidth={2} />
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-xs font-medium text-foreground">{userEmail}</p>
                  <p className="text-xs text-muted-foreground">Administrator</p>
                </div>
                <Icon 
                  name={showUserMenu ? "ChevronUp" : "ChevronDown"} 
                  size={16} 
                  strokeWidth={2} 
                  color="var(--color-foreground)"
                />
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-card border border-border rounded-lg shadow-elevation-2 py-1 z-50">
                  <div className="px-4 py-3 border-b border-border">
                    <p className="text-sm font-medium text-foreground">{userEmail}</p>
                    <p className="text-xs text-muted-foreground">Administrator Account</p>
                  </div>
                  <button
                    onClick={() => {
                      handleNavigate('/fleet-operations-dashboard');
                    }}
                    className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-foreground hover:bg-accent transition-colors"
                  >
                    <Icon name="LayoutDashboard" size={16} strokeWidth={2} />
                    <span>Dashboard</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Icon name="LogOut" size={16} strokeWidth={2} />
                    <span>{isLoggingOut ? 'Logging out...' : 'Logout'}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;